import { Branch } from '../../../../dorian-shared/types/branch/Branch';
import { EpisodeTextImportNode } from '../../../../services/EpisodeTextService/models/EpisodeTextImportNode';

/**
 * Collect all gotoBranchId targets from a Branch, including:
 * - branch.gotoBranchId (main goto)
 * - branch.answers[].gotoBranchId (choices at branch level)
 * - branch.switch[].gotoBranchId (check/switch at branch level)
 * - branch.steps[].answers[].gotoBranchId (choices inside steps)
 * - branch.steps[].switch[].gotoBranchId (check/switch inside steps)
 */
function collectChildIds(branch: Branch): number[] {
  const children: number[] = [];
  const seen = new Set<number>();

  const add = (id: number | undefined | null) => {
    if (id != null && id !== 0 && id !== branch.id && !seen.has(id)) {
      seen.add(id);
      children.push(id);
    }
  };

  // Branch-level answers
  if (Array.isArray(branch.answers)) {
    for (const answer of branch.answers) {
      add((answer as { gotoBranchId?: number }).gotoBranchId);
    }
  }

  // Branch-level switch
  if (Array.isArray(branch.switch)) {
    for (const sw of branch.switch) {
      add(sw.gotoBranchId);
    }
  }

  // Step-level answers and switch
  if (Array.isArray(branch.steps)) {
    for (const step of branch.steps) {
      if (Array.isArray(step.answers)) {
        for (const answer of step.answers) {
          add(answer.gotoBranchId);
        }
      }
      if (Array.isArray(step.switch)) {
        for (const sw of step.switch) {
          add(sw.gotoBranchId);
        }
      }
    }
  }

  // Branch main goto (fallthrough) — last, so choices/checks come first in DFS
  add(branch.gotoBranchId);

  return children;
}

/**
 * Sort episode nodes by approximate player flow using DFS traversal.
 *
 * 1. Build adjacency list from all goto sources in Branch data
 * 2. Find intro node (lowest branch ID)
 * 3. DFS from intro — each node visited exactly once
 * 4. Orphan nodes (unreachable) placed at the beginning, sorted by ID
 */
export function sortNodesByPlayerFlow(
  importNodes: EpisodeTextImportNode[],
  branches: Branch[],
): EpisodeTextImportNode[] {
  if (importNodes.length <= 1) return importNodes;

  // Map realBranchId → import node
  const nodeByBranchId = new Map<number, EpisodeTextImportNode>();
  for (const node of importNodes) {
    if (node.realBranchId != null) {
      nodeByBranchId.set(node.realBranchId, node);
    }
  }

  // Build adjacency list
  const adjacency = new Map<number, number[]>();
  for (const branch of branches) {
    adjacency.set(branch.id, collectChildIds(branch));
  }

  // Find intro — lowest branch ID
  let startId = Infinity;
  for (const node of importNodes) {
    if (node.realBranchId != null && node.realBranchId < startId) {
      startId = node.realBranchId;
    }
  }
  if (startId === Infinity) return importNodes;

  // DFS — iterative to avoid stack overflow on large episodes
  const visited = new Set<number>();
  const ordered: EpisodeTextImportNode[] = [];
  const stack: number[] = [startId];

  while (stack.length > 0) {
    const branchId = stack.pop()!;
    if (visited.has(branchId)) continue;
    visited.add(branchId);

    const node = nodeByBranchId.get(branchId);
    if (node) {
      ordered.push(node);
    }

    // Push children in reverse so first child is processed first (stack is LIFO)
    const children = adjacency.get(branchId) ?? [];
    for (let i = children.length - 1; i >= 0; i--) {
      if (!visited.has(children[i])) {
        stack.push(children[i]);
      }
    }
  }

  // Orphans — not reached by DFS, sorted by ID, placed at the beginning
  const orphans = importNodes.filter(
    (n) => n.realBranchId != null && !visited.has(n.realBranchId),
  ).sort((a, b) => (a.realBranchId ?? 0) - (b.realBranchId ?? 0));

  // Nodes without realBranchId (safety fallback)
  const noId = importNodes.filter((n) => n.realBranchId == null);

  return [...orphans, ...noId, ...ordered];
}
